import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form"
import { toast } from "@hooks/use-toast"
import { useAuth } from "@stores/auth-store"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { DictType } from "@pma/types"
import { cn } from "@pma/utils/helpers"
import PmsLoader from "@components/common/loader"
import useTranslator from "@hooks/use-translator"
import axios from "axios"
import { COMPANY_SETTINGS_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { setCompanyAccess } from "@pma/utils/utils"
import { Switch } from "@components/common/switch"
import { permissionsDescriptions } from "@pma/utils/data"
import { Info } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import UnauthorizedCard from "@components/layout/unauthorized-card"

const FormSchema = z.object({
  options: z.array(z.string()).refine((value) => value.some((item) => item), {
    message: "No changes detected",
  }),
})

const WorkspaceSettings = () => {
  const [defaultOptions, setDefaultOptions] = useState([""])
  const { authConfig } = useAuth()

  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const orgId = authConfig?.last_organization_id
  const getOrganizationSettings = async () => {
    const response = await axios.get(
      SERVER_URL + COMPANY_SETTINGS_ENDPOINT + `?organization_id=${orgId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const { data, isFetched, refetch, isLoading, error } = useQuery({
    queryKey: ["company-settings"],
    queryFn: getOrganizationSettings,
  })

  const companySettings = useMemo(() => {
    const excluded = ["id", "created_at", "organization_user_id"]
    if (isFetched && data) {
      return Object.keys(data)
        ?.map((key) => {
          if (!excluded.includes(key)) {
            if (data[key]) {
              setDefaultOptions((prev) => [...prev, key])
            }
            return {
              name: key,
              status: data[key],
            }
          }
          return null
        })
        .filter((item) => item)
    }

    return []
  }, [isFetched])

  const companySettingsGrouping = useMemo(() => {
    const memberSettings: DictType[] | null = [
      {
        title: "Projects",
        permissions: [],
      },
      {
        title: "Organization",
        permissions: [],
      },
      // {
      //   title: "Teams",
      //   permissions: [],
      // },
      {
        title: "Files and folders",
        permissions: [],
      },
      {
        title: "Notification settings",
        permissions: [],
      },
    ]

    companySettings?.map((setting) => {
      if (setting?.name == "teams_edit_only_organization_account") {
        return
      }
      if (
        (setting?.name.includes("diary") || setting?.name.includes("project")) &&
        !setting?.name.includes("file") &&
        !setting?.name.includes("folder")
      ) {
        memberSettings[0].permissions.push({
          id: setting?.name,
          name: setting?.name,
        })
      }
      if (
        setting?.name.includes("organization") &&
        !setting?.name.includes("team") &&
        !setting?.name.includes("project")
      ) {
        memberSettings[1].permissions.push({
          id: setting?.name,
          name: setting?.name,
        })
      }
      // if (setting?.name.includes("teams")) {
      //   memberSettings[2].permissions.push({
      //     name: setting?.name,
      //     id: setting?.name,
      //   })
      // }
      if (setting?.name.includes("file") || setting?.name.includes("folder")) {
        memberSettings[2].permissions.push({
          id: setting?.name,
          name: setting?.name,
        })
      }
      if (setting?.name.includes("inform")) {
        memberSettings[3].permissions.push({
          id: setting?.name,
          name: setting?.name,
        })
      }

      return memberSettings
    })

    return memberSettings
  }, [companySettings])

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      options: defaultOptions,
    },
  })

  const handleCheckboxChange = (checked: boolean, field: DictType, name: string) => {
    let updatedOptions: string[]
    if (checked) {
      updatedOptions = [...(defaultOptions ?? []), name]
    } else {
      updatedOptions = defaultOptions.filter((value: string) => value !== name) || []
    }

    setDefaultOptions([...new Set(updatedOptions)])
    return checked
      ? field.onChange([...new Set(updatedOptions)])
      : field.onChange([...new Set(updatedOptions)])
  }

  const updateCompanySettingsMutation = useMutation({
    mutationKey: ["update-company-settings"],
    mutationFn: (companySettings: DictType) => setCompanyAccess(companySettings),
    onSuccess: (data) => {
      refetch()
      toast({
        title: translate("Success"),
        description: translate("Company settings updated"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorResponse = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorResponse || "Unable to update company settings"),
        variant: "error",
      })
    },
  })

  function onSubmit(data: z.infer<typeof FormSchema>) {
    const selectedOptions = data?.options

    const payload: DictType = {}
    companySettings.map((option) => {
      return (payload[String(option?.name)] = selectedOptions.includes(option?.name || "")
        ? true
        : false)
    })
    updateCompanySettingsMutation.mutate({
      ...payload,
      organization_id: orgId,
    })
  }

  const errorResponse = error as DictType
  const isUnauthorized = errorResponse?.request?.status == 403

  useEffect(() => {
    errorResponse?.response &&
      toast({
        title: translate("Error"),
        description: translate(
                errorResponse?.response?.data?.message || "You don't have required permissions",
              ),
        variant: "error",
      })
  }, [errorResponse?.response])

  if (isLoading) {
    return <PmsLoader />
  }

  return (
    <>
      {isUnauthorized ? (
        <UnauthorizedCard />
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <div className='mb-4'>
              <FormDescription>{translate("Customize organization preferences")}</FormDescription>
              <FormDescription>
                {isHebrew
                  ? `${translate("Role-based permissions take precedence over organization preferences.")} :${translate("Note")}`
                  : `${translate("Note")}: ${translate("Role-based permissions take precedence over organization preferences.")}`}
              </FormDescription>
            </div>
            <div className='space-y-8'>
              {companySettingsGrouping &&
                companySettingsGrouping?.map((group: DictType) => (
                  <div key={group?.title} className='space-y-2'>
                    <h3 className='text-sm font-semibold'>{translate(group?.title)}</h3>
                    {["Projects", "Files and folders"].includes(group?.title) ? (
                      <small>
                        {isHebrew
                          ? `.${translate("These rules will be applied only to users without assigned roles")}`
                          : `${translate("These rules will be applied only to users without assigned roles")}.`}
                      </small>
                    ) : null}
                    <div className='grid grid-cols-1 gap-x-3 gap-y-5 sm:grid-cols-2 md:grid-cols-3 md:gap-y-6'>
                      {group?.permissions?.map((permission: DictType) => (
                        <FormField
                          key={permission?.name}
                          control={form.control}
                          name='options'
                          render={({ field }) => {
                            return (
                              <FormItem key={permission?.name}>
                                <FormLabel
                                  className={cn(
                                    "p-0 font-thin capitalize tracking-wide",
                                    defaultOptions?.includes(permission?.name || "") &&
                                      "font-normal",
                                  )}
                                >
                                  {isHebrew
                                    ? `.${translate(permission?.name?.replaceAll("_", " ") || "")}`
                                    : `${translate(permission?.name?.replaceAll("_", " ") || "")}.`}
                                </FormLabel>
                                <FormControl>
                                  <button type='submit' className='flex items-center justify-start'>
                                    <div className='flex gap-6'>
                                      <Switch
                                        checked={defaultOptions?.includes(permission?.name || "")}
                                        onCheckedChange={(checked) =>
                                          handleCheckboxChange(
                                            !!checked,
                                            field,
                                            permission?.name || "",
                                          )
                                        }
                                        className='h-6 w-12'
                                      />
                                      <Tooltip>
                                        <TooltipTrigger>
                                          <Info size={16} className='text-slate-500' />
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {translate(
                                            permissionsDescriptions[`${permission?.name}`],
                                          )}
                                        </TooltipContent>
                                      </Tooltip>{" "}
                                    </div>
                                  </button>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )
                          }}
                        />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </form>
        </Form>
      )}
    </>
  )
}

export default WorkspaceSettings
